import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faComments, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown, StatusBadge, outOfOfficeForAssignee } from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';
import { RiskCategoryEnum, getRiskCategoryCycleHint } from '../enums/riskCategory';
import { ContentErrorBlock } from './tabs/Tile';
import { ContentType } from './tabs/contentTypes';
import ViewerCountBadge from '../components/ViewerCountBadge';

const DocumentToolbar = React.memo(({
  doc,
  user,
  activeUsers,
  hasViewers,
  getViewerCount,
  getViewers,
  assignee,
  assigneeSelectedIcon,
  assigneeSelectedOutOfOffice,
  assigneeOptions,
  assigneeDropdownOpen,
  onToggleAssigneeDropdown,
  onAssigneeChange,
  canEditAssignee,
  canEditRiskCategory,
  onRiskCategoryClick,
  isCommentsVisible,
  onToggleComments,
  showWorkflowRunsButton,
  workflowLogsCount,
  onShowWorkflowLogs,
  isPatientDiaryVisible,
  onTogglePatientDiary,
  showPatientDiaryToggle,
  pendingReferralsCount,
  navigationContext,
  onNavigatePrev,
  onNavigateNext,
  prevButtonAnimating,
  nextButtonAnimating,
  isRegenerationEnabled,
  handleRegenerationClick
}) => {
  // Get risk category only if it exists, no default
  const riskCategory = doc?.riskCategory ? RiskCategoryEnum[doc.riskCategory] : null;

  // Navigation button states
  const canNavigatePrev = navigationContext && navigationContext.currentIndex > 0;
  const canNavigateNext = navigationContext && navigationContext.currentIndex < navigationContext.documentIds.length - 1;

  let displayStatus;
  if (doc.status === StatusEnum.READY.value) {
    if (hasViewers && hasViewers(doc.documentId)) {
      displayStatus = StatusEnum.IN_REVIEW;
    } else if (outOfOfficeForAssignee(doc.assigneeId, activeUsers)) {
      displayStatus = StatusEnum.ASSIGNED_TO_OOO;
    } else if (doc.assigneeId) {
      displayStatus = StatusEnum.ASSIGNED;
    } else {
      displayStatus = StatusEnum.READY;
    }
  } else {
    displayStatus = StatusEnum[doc.status];
  }

  return (
    <div className="document-toolbar">
      <div className="toolbar-left">
        <CustomDropdown
          searchable
          searchPlaceholder="Search assignees..."
          selected={assignee.name}
          selectedIcon={assigneeSelectedIcon}
          selectedOutOfOffice={assigneeSelectedOutOfOffice}
          options={assigneeOptions}
          isOpen={assigneeDropdownOpen}
          toggleDropdown={onToggleAssigneeDropdown}
          onSelect={onAssigneeChange}
          keyExtractor={(option, index) => `assignee-${option.id || index}`}
          disabled={!canEditAssignee}
          maxHeight="500px"
        />
        <button
          className={`document-comment-toggle ${isCommentsVisible ? 'active' : ''}`}
          onClick={onToggleComments}
          title="Toggle comments"
        >
          <FontAwesomeIcon icon={faComments} /> Comments
          {doc?.comments && doc.comments.length > 0 && (
            <span className="comment-count-badge">{doc.comments.length}</span>
          )}
        </button>
        {showWorkflowRunsButton && (
          <button
            className="document-comment-toggle"
            onClick={onShowWorkflowLogs}
            title="View workflow runs"
          >
            <FontAwesomeIcon icon={faProjectDiagram} /> Workflow Runs
            <span className="comment-count-badge">{workflowLogsCount}</span>
          </button>
        )}
        <StatusBadge
          label={displayStatus?.description || doc.status}
          colorFamily={displayStatus?.colorFamily || 'gray'}
          size="normal"
        />
        {riskCategory && (
          <StatusBadge
            label={riskCategory.displayName}
            colorFamily={riskCategory.colorFamily || 'gray'}
            size="normal"
            className="risk-category-badge"
            tooltip={(
              <>
                <span className="risk-category-tooltip-lead">
                  <FontAwesomeIcon icon={riskCategory.icon} />
                  {riskCategory.description}
                </span>
                {canEditRiskCategory && (
                  <span className="risk-category-tooltip-hint">{getRiskCategoryCycleHint(riskCategory.value)}</span>
                )}
              </>
            )}
            onClick={canEditRiskCategory ? onRiskCategoryClick : undefined}
          />
        )}
        <ContentErrorBlock
          doc={doc}
          contentType={ContentType.RISK_CATEGORY_STATUS}
          isRetryEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
          onRegenerateClick={handleRegenerationClick}
          compact
        />
      </div>
      <div className="toolbar-right">
        {navigationContext && (
          <>
            <button
              className={`header-button nav-button${prevButtonAnimating ? ' nav-button-animating' : ''}`}
              onClick={onNavigatePrev}
              disabled={!canNavigatePrev}
              title={canNavigatePrev ? "Previous document" : "No previous document"}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Prev
            </button>
            <span className="nav-position">
              {navigationContext.currentIndex + 1} / {navigationContext.documentIds.length}
            </span>
            <button
              className={`header-button nav-button${nextButtonAnimating ? ' nav-button-animating' : ''}`}
              onClick={onNavigateNext}
              disabled={!canNavigateNext}
              title={canNavigateNext ? "Next document" : "No next document"}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </>
        )}
        <ViewerCountBadge
          itemId={doc.documentId}
          getViewerCount={getViewerCount}
          getViewers={getViewers}
          activeUsers={activeUsers}
          user={user}
          itemType="document"
        />
        {showPatientDiaryToggle && (
          <button
            className={`patient-diary-toggle ${isPatientDiaryVisible ? 'active' : ''}`}
            onClick={onTogglePatientDiary}
            disabled={!doc?.patientId}
            title={!doc?.patientId ? "No patient matched" : "Toggle patient diary"}
          >
            Patient Diary
            {pendingReferralsCount > 0 && (
              <span className="notification-badge">{pendingReferralsCount}</span>
            )}
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
    </div>
  );
});

export default DocumentToolbar;
